import { Link } from "react-router-dom";
import { ShieldCheck, FileSearch, ClipboardCheck, Award, Lock, ArrowRight, ScrollText } from "lucide-react";

const pillars = [
  { icon: FileSearch, title: "Medical Chronologies", desc: "Chronological, source-cited timelines built from complete records — court-ready.", tag: "01" },
  { icon: ClipboardCheck, title: "Merit & Case Screening", desc: "Objective RN assessment of causation, damages potential, and litigation viability.", tag: "02" },
  { icon: ScrollText, title: "Standards of Care Research", desc: "Evidence-based standards, deviations, and supporting literature.", tag: "03" },
  { icon: Award, title: "VA Claim Record Review", desc: "Support for VA-accredited attorneys and agents on disability and claims medical records.", tag: "04" },
];

export function HeroSection() {
  return (
    <section className="relative overflow-hidden border-b border-slate-200">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#0A192F]/5" />
        <div className="absolute bottom-0 left-0 right-0 h-1 stripe-diag" />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="text-caption text-[#B91C1C] mb-5" data-testid="hero-eyebrow">RN LEGAL NURSE CONSULTING · VETERANS & LEGAL</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.05] text-[#0A192F]">
            Precision medical review<span className="text-[#B91C1C]">.</span><br />
            Grounded in the record. <br />
            <span className="text-slate-600 italic font-normal">Answerable to the case.</span>
          </h1>
          <p className="mt-7 text-lg text-slate-600 max-w-xl leading-relaxed">
            We serve attorneys and VA-accredited professionals with rigorous medical chronologies, merit reviews, and record analysis — engineered for federal-grade confidentiality.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/case-intake" data-testid="hero-cta-refer" className="btn-navy inline-flex items-center gap-2 px-6 py-3 text-[13px] uppercase tracking-widest">
              Refer a Case <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-300 text-[13px] uppercase tracking-widest hover:border-[#0A192F]">
              Explore Services
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            <span className="badge-federal">HIPAA-Aligned Architecture</span>
            <span className="badge-federal">Encrypted Vault</span>
            <span className="badge-gold">Human RN Review Required</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative border border-slate-900">
            <img
              src="https://images.unsplash.com/photo-1758691462814-485c3672e447?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="RN reviewing medical records"
              className="w-full h-[480px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-900 p-5 max-w-[220px] hidden md:block">
              <div className="text-caption text-[#B91C1C] mb-1">RN CERTIFIED</div>
              <div className="text-sm text-slate-800 leading-snug">Every deliverable is reviewed and signed by a licensed Registered Nurse.</div>
            </div>
            <div className="absolute top-4 right-4 badge-federal !bg-white/90">Case File · IFNA</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ServicesPillars() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <div className="gold-underline mb-4" />
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F]">The four pillars of our practice</h2>
        </div>
        <p className="max-w-md text-slate-600 leading-relaxed">Independent, precise, and defensible medical analysis — the work product attorneys and VA-accredited professionals rely on.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {pillars.map((s, i) => (
          <div key={s.title} className={`card-federal p-7 fade-in-up stagger-${i + 1}`} data-testid={`service-card-${i}`}>
            <div className="flex items-center justify-between mb-6">
              <s.icon className="w-6 h-6 text-[#0A192F]" strokeWidth={1.6} />
              <span className="font-mono text-xs text-slate-400">{s.tag}</span>
            </div>
            <h3 className="font-serif font-bold text-lg text-[#0A192F] mb-2">{s.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function VeteransSection() {
  return (
    <section className="bg-[#0A192F] text-white relative">
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#B91C1C]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <img src="https://images.pexels.com/photos/7467917/pexels-photo-7467917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Veteran medical consultation" className="w-full border border-white/10 h-[420px] object-cover" />
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="text-caption text-[#D97706] mb-4">VETERANS & DOD-FACING PRACTICE</div>
          <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-6 leading-tight">Built for the sensitivity of Veterans medical & military information.</h2>
          <p className="text-slate-300 leading-relaxed text-lg mb-6">Our workflows separate PHI, PII, VA-related information, and potentially CUI — with encryption in transit and at rest, least-privilege access, audit trails, and an architecture path capable of deployment to appropriately authorized federal cloud services.</p>
          <ul className="space-y-3 text-slate-300 text-[15px]">
            {["HIPAA-aligned technical safeguards","NIST 800-171 & CMMC design targets","Data segregation & retention controls","Comprehensive audit logging"].map((x, i) => (
              <li key={x} className="flex gap-3 items-start" data-testid={`veteran-benefit-${i}`}>
                <ShieldCheck className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />{x}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link to="/security" className="inline-flex gap-2 items-center border border-white/40 px-5 py-3 text-[12px] uppercase tracking-widest hover:bg-white hover:text-[#0A192F]" data-testid="cta-security">
              View Security & Compliance <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AIAssistSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <div className="gold-underline mb-4" />
        <div className="text-caption text-[#B91C1C] mb-3">DISCIPLINED WORKFLOW · RN-DECIDED</div>
        <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F] leading-tight mb-6">A defined process — every deliverable reviewed by a Registered Nurse.</h2>
        <p className="text-slate-600 text-lg leading-relaxed mb-6">From conflict clearance through final delivery, every engagement follows a documented workflow: secure intake, record organization and indexing, RN analysis, and signed work product with source-grounded citations.</p>
        <div className="flex flex-wrap gap-3">
          <span className="badge-federal">Source-grounded citations</span>
          <span className="badge-crimson">Mandatory RN review</span>
          <span className="badge-gold">Granular permissions</span>
        </div>
      </div>
      <div className="lg:col-span-6">
        <div className="border border-slate-900 bg-white overflow-hidden">
          <div className="bg-[#0A192F] text-white px-5 py-3 flex items-center justify-between">
            <div className="text-caption text-white/70">CASE WORKFLOW · CHRONOLOGY EXCERPT</div>
            <Lock className="w-4 h-4 text-[#D97706]" />
          </div>
          <div className="p-6 font-mono text-[12.5px] leading-relaxed text-slate-700 space-y-2">
            <div>2019-04-12 | Dr. Nakamura | Initial evaluation — PTSD screening positive</div>
            <div>2019-06-03 | VA Medical Center | Neurology consult, TBI workup ordered</div>
            <div>2019-08-27 | Dr. Patel | MRI: mild hippocampal atrophy noted</div>
            <div className="text-slate-400">2020-01-04 | <span className="text-[#B91C1C]">⚠ gap detected — no records for 5 months</span></div>
            <div>2020-06-14 | VA Regional | Rating decision issued</div>
            <div className="border-t border-slate-200 pt-3 mt-3 text-slate-500 italic">RN-reviewed and signed before delivery.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FounderStrip() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-3">
        <div className="relative border border-slate-900">
          <img src="/rn-legal-nurse-consultant.png" alt="RN Legal Nurse Consultant" className="w-full aspect-square object-cover" data-testid="home-founder-photo" />
          <div className="absolute bottom-0 left-0 right-0 bg-[#0A192F] px-3 py-2">
            <div className="text-caption text-white/60 text-[9px]">FOUNDER · RN</div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-9">
        <div className="text-caption text-[#B91C1C] mb-3">YOUR RN LEGAL NURSE CONSULTANT</div>
        <h2 className="text-2xl lg:text-3xl font-serif font-bold text-[#0A192F] leading-tight mb-4">One Registered Nurse. Direct access. Signed work product.</h2>
        <p className="text-slate-600 leading-relaxed max-w-2xl mb-5">You work directly with the RN who reviews your records — from conflict clearance through the final, RN-validated deliverable. No handoffs, no anonymous review pool.</p>
        <Link to="/about" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest text-[#B91C1C] link-underline" data-testid="cta-meet-founder">Meet the founder <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </section>
  );
}

export function CtaBand() {
  return (
    <section className="bg-[#F1F5F9] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-2">
          <h2 className="text-2xl lg:text-3xl font-serif font-bold text-[#0A192F] leading-tight">Refer a case for a merit review — encrypted intake, 48-hour first response.</h2>
        </div>
        <div className="flex md:justify-end">
          <Link to="/case-intake" data-testid="cta-band-refer" className="btn-crimson inline-flex items-center gap-2 px-6 py-3 text-[13px] uppercase tracking-widest">
            Start Secure Intake <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
